import { take, put, select, all, takeLatest, actionChannel, call } from "redux-saga/effects";
import {
    CONFIGURATION_LOADED,
    ADD_ITEM,
    SET_MENU_ITEMS,
    SET_SELECTED_MENU,
    SET_CONFIGURATION,
    SET_SUMMARY_ITEMS,
    SET_MENU_STRUCTURE,
    PREVIEW_ITEM,
    SET_ITEM_STATES,
    REMOVE_ITEM,
    SET_PREVIEW_ITEM,
    CLEAR_PREVIEW_ITEM,
    AddItemAction,
} from "../../actions/configurationActions";
import {
    getSessionId,
    getActiveFrame,
    getActiveImageSeriesId,
    getItemStates,
    getSummaryItems,
    getLanguage,
    getPreviewItem,
    getPreviewConfig,
    getItemMap,
} from "../selectors";

import client from "../../../api/client/SdsWebApiClient";

import { SET_SESSION_DATA } from "../../actions/sessionActions";

import { ConfigEncoding, LoadConfigResponse, MenuNodeType } from "../../../api/generated";
import { Item } from "../../../models/item";
import { ChangeConfigResponse } from "../../../api/generated/models/ChangeConfigResponse";
import { ItemStates } from "../../../models/itemStates";
import { Delimiter } from "../../../models/delimiter";
import { SummaryItem } from "../../../models/summaryItem";
import { PreferredAngles } from "../../../models/preferredAngles";
import { PreviewConfig } from "../../../models/previewConfig";

function addSummaryItem(
    item: Item,
    summaryItems: SummaryItem[],
    language: string,
    increaseAmount: boolean
): SummaryItem[] {
    // Look through the summary items and increase the amount
    // if it already exists, or add it otherwise.
    let found = false;
    const newSummaryItems = summaryItems.map((summaryItem): SummaryItem => {
        if (summaryItem.item.id !== item.id) return summaryItem;

        found = true;
        return increaseAmount ? new SummaryItem(item, summaryItem.amount + 1) : summaryItem;
    });
    if (!found) {
        const locale = language.toLowerCase();
        newSummaryItems.push(new SummaryItem(item));
        newSummaryItems.sort((a, b) => {
            if (a.item.group !== b.item.group) return a.item.group.localeCompare(b.item.group, "en");
            return a.summaryText.localeCompare(b.summaryText, locale);
        });
    }
    return newSummaryItems;
}

/* eslint-disable no-param-reassign */
export function* loadConfigurationSaga(): any {
    yield take(SET_SESSION_DATA);

    const searchParams = new URLSearchParams(window.location.search);
    let config = searchParams.get("start_config");
    const encoding = config ? ConfigEncoding.External : ConfigEncoding.Default;
    config = config || searchParams.get("config_string") || "224-MY20";
    if (!config) {
        throw Error("The configuration has not been set!");
    }

    const sessionId: string = yield select(getSessionId);
    if (!sessionId) {
        throw Error("The session has not been created!");
    }

    const response: LoadConfigResponse = yield client.loadConfigFromStringPost(sessionId, config, encoding);
    const language = yield select(getLanguage);
    let { configurationString } = response;
    let itemStates = new ItemStates(response.items);
    const menuItems: { [key: string]: (Item | Delimiter)[] } = {};
    const allItemsByInternalId: { [key: string]: Item } = {};
    const allItemsByInternalIdUpper: { [key: string]: Item } = {};
    const allItemsByRetailerIdUpper: { [key: string]: Item } = {};
    response.menus.forEach(menu => {
        if (menu.children) {
            menuItems[menu.id] = menu.children.reduce((convertedItems: (Item | Delimiter)[], n) => {
                if (n.nodeType === MenuNodeType.Item) {
                    if (!n.group) throw new Error(`Item ${n.id} doesn't belong to a group.`);

                    const preferredAngles: PreferredAngles | null =
                        n.preferredSeries && n.preferredFrames && n.preferredFrames.length > 0
                            ? { series: n.preferredSeries, frames: n.preferredFrames }
                            : null;

                    const item = new Item(n.id, n.group, n.displayText, n.retailerId, n.isPermanent, preferredAngles);
                    allItemsByInternalId[item.id] = item;
                    allItemsByInternalIdUpper[item.id.toLocaleUpperCase(language)] = item;
                    if (item.retailerId) allItemsByRetailerIdUpper[item.retailerId.toLocaleUpperCase(language)] = item;
                    convertedItems.push(item);
                } else if (n.nodeType === MenuNodeType.Separator) {
                    convertedItems.push(new Delimiter(n.id, n.displayText));
                }
                return convertedItems;
            }, []);
        }
    });

    let frame = yield select(getActiveFrame);
    const seriesId = yield select(getActiveImageSeriesId);

    const series =
        response.imageSeries.find(s => s.id === seriesId) ||
        response.imageSeries.find(s => s.id === "EXTERIOR") ||
        response.imageSeries[0];

    if (frame === null || !series.frames.includes(frame)) {
        [frame] = series.frames;
    }

    // Add accessories to summary and activate one from each group.
    let summaryItems: SummaryItem[] = [];
    const accessories = searchParams
        .get("accessories")
        ?.split(",")
        .map(a => a.trim())
        .filter(a => a);

    if (accessories) {
        const activatedGroups = new Set();
        for (let i = 0; i < accessories.length; i += 1) {
            const id = accessories[i];
            const idUpper = id.toLocaleUpperCase(language);
            const item = allItemsByRetailerIdUpper[idUpper] || allItemsByInternalIdUpper[idUpper];
            if (!item) {
                // eslint-disable-next-line no-console
                console.warn(`Accessory ${id} not found`);
                continue;
            }

            summaryItems = addSummaryItem(item, summaryItems, language, true);

            if (!activatedGroups.has(item.group)) {
                activatedGroups.add(item.group);
                const changeResponse: ChangeConfigResponse = yield client.changeConfigItemPost(sessionId, item.id);
                itemStates = new ItemStates(changeResponse.items);
                configurationString = changeResponse.configurationString;
            }
        }
    }

    yield all([
        put({ type: SET_MENU_ITEMS, menuItems }),
        put({ type: SET_MENU_STRUCTURE, menuStructure: response.menus }),
        put({ type: SET_SUMMARY_ITEMS, summaryItems }),
        put({ type: SET_SELECTED_MENU, selectedMenu: null }),
        put({
            type: CONFIGURATION_LOADED,
            configurationString,
            modelId: response.model,
            itemStates,
            imageSeries: response.imageSeries,
            activeSeriesId: series.id,
            activeFrame: frame,
            itemMap: allItemsByInternalId,
        }),
    ]);
}

function* performChangeConfigItem(itemId: string): any {
    const sessionId: string = yield select(getSessionId);
    const response: ChangeConfigResponse = yield client.changeConfigItemPost(sessionId, itemId);
    const itemMap: { [key: string]: Item } = yield select(getItemMap);

    const summaryItems: SummaryItem[] = yield select(getSummaryItems);
    let newSummaryItems = summaryItems;
    response.removed.forEach(removedId => {
        const item = itemMap[removedId];
        if (item && item.isPermanent) {
            newSummaryItems = newSummaryItems.filter(s => s.item.id !== removedId);
        }
    });
    if (newSummaryItems.length < summaryItems.length) {
        yield put({ type: SET_SUMMARY_ITEMS, summaryItems: newSummaryItems });
    }

    const newItemStates = new ItemStates(response.items);
    yield all([
        put({
            type: SET_CONFIGURATION,
            configurationString: response.configurationString,
            previewConfig: null,
        }),
        put({ type: SET_ITEM_STATES, itemStates: newItemStates }),
    ]);
}

export function* addItemSaga(): any {
    // Queue the actions, and process them in sequential order.
    const channel = yield actionChannel(ADD_ITEM);
    while (true) {
        const action = yield take(channel);
        const { item, requiredItems }: AddItemAction = action;
        const itemStates: ItemStates = yield select(getItemStates);
        const summaryItems: SummaryItem[] = yield select(getSummaryItems);

        const language: string = yield select(getLanguage);
        let newSummaryItems = addSummaryItem(item, summaryItems, language, true);
        if (requiredItems) {
            requiredItems.forEach(it => {
                newSummaryItems = addSummaryItem(it, newSummaryItems, language, false);
            });
        }
        yield put({ type: SET_SUMMARY_ITEMS, summaryItems: newSummaryItems });

        if (!itemStates.isSelected(item.id)) {
            yield call(performChangeConfigItem, item.id);
        } else {
            const previewConfig: string = yield select(getPreviewConfig);
            if (previewConfig) {
                yield put({ type: SET_CONFIGURATION, previewConfig: null });
            }
        }
    }
}

export function* removeItemSaga(): any {
    // Queue the actions, and process them in sequential order.
    const channel = yield actionChannel(REMOVE_ITEM);
    while (true) {
        const { item }: { item: Item } = yield take(channel);
        const itemStates: ItemStates = yield select(getItemStates);
        const summaryItems: SummaryItem[] = yield select(getSummaryItems);
        const newSummaryItems = summaryItems.filter(s => s.item.id !== item.id);
        yield put({ type: SET_SUMMARY_ITEMS, summaryItems: newSummaryItems });

        // It's not certain that the item is active in the visual configuration
        // when having multiple items of the same type.
        if (itemStates.isSelected(item.id)) {
            // Activate another selected item in the same group if found,
            // otherwise we send the same id to use toggle bahavior in backend.
            const id = newSummaryItems.find(s => s.item.group === item.group)?.item.id || item.id;
            yield call(performChangeConfigItem, id);
        }
    }
}

export function* previewItemSaga(): any {
    // Queue all activations, and process them in sequential order.
    const channel = yield actionChannel(PREVIEW_ITEM);
    while (true) {
        const { item }: { item: Item } = yield take(channel);
        const previewItem: Item = yield select(getPreviewItem);
        if (previewItem && item.id === previewItem.id) {
            continue;
        }

        const itemStates: ItemStates = yield select(getItemStates);
        const summaryItems: SummaryItem[] = yield select(getSummaryItems);
        yield put({ type: SET_PREVIEW_ITEM, item });
        if (itemStates.isSelected(item.id)) {
            // If it's already selected in the non-preview config, just set the preview config to null.
            yield put({ type: SET_CONFIGURATION, previewConfig: null });
        } else if (summaryItems.some(s => s.item.id === item.id)) {
            // If it's in the summary, activate it instead of just previewing it.
            yield call(performChangeConfigItem, item.id);
        } else {
            // Preview the config change.
            const sessionId: string = yield select(getSessionId);
            const response: ChangeConfigResponse = yield client.previewChangeConfigItemPost(sessionId, item.id);
            const previewConfig: PreviewConfig = {
                configurationString: response.configurationString,
                addedItems: response.added,
                removedItems: response.removed,
            };
            yield put({
                type: SET_CONFIGURATION,
                previewConfig,
            });
        }
    }
}

export function* clearPreviewItemSaga(): any {
    yield takeLatest(CLEAR_PREVIEW_ITEM, function* () {
        yield all([
            put({ type: SET_CONFIGURATION, previewConfig: null }),
            put({ type: SET_PREVIEW_ITEM, previewItem: null }),
        ]);
    });
}
